import { LocalProjection } from "./geo";
import { otherEnd, type Graph } from "./graph";
import { Flag, Hw, Surface } from "./tags";

/**
 * Street features for the runnability model (ADR 0007): road crossings and traffic lights as a
 * cost at graph nodes, in metres of running.
 */

/**
 * Metre-equivalent cost of crossing a road of this class at a junction (a short wait and a look).
 * A traffic light adds SIGNAL_M on top.
 */
export const CROSS_M: Readonly<Partial<Record<number, number>>> = {
  [Hw.LivingStreet]: 0,
  [Hw.Service]: 1.5,
  [Hw.Residential]: 4,
  [Hw.Unclassified]: 5,
  [Hw.Tertiary]: 12,
  [Hw.Secondary]: 20,
  [Hw.Primary]: 30,
};
/**
 * A red light averages 20–25 s of waiting (about 60 m of running); charged at half that. The live
 * A/B (ADR 0007) found the full cost pulls routes off streets real runners use (trace agreement
 * 61% → 53%); half keeps 57% and still halves lights per km (0.91 → 0.44).
 */
export const SIGNAL_M = 30;
/** Lights snap to the nearest graph node within this distance. */
export const SIGNAL_SNAP_M = 15;
/** A crossing of a road at least this busy counts as "busy" on route cards. */
const BUSY = Hw.Tertiary;

const ROAD_RANK: Readonly<Partial<Record<number, number>>> = {
  [Hw.LivingStreet]: 1,
  [Hw.Service]: 2,
  [Hw.Residential]: 3,
  [Hw.Unclassified]: 4,
  [Hw.Tertiary]: 5,
  [Hw.Secondary]: 6,
  [Hw.Primary]: 7,
};

const RANK_COUNT = new Uint16Array(8);
/** Road class for each rank in ROAD_RANK. */
const RANK_HW: readonly number[] = [
  0,
  Hw.LivingStreet,
  Hw.Service,
  Hw.Residential,
  Hw.Unclassified,
  Hw.Tertiary,
  Hw.Secondary,
  Hw.Primary,
];

/**
 * Marks `g.signal[n] = 1` for the nearest node within SIGNAL_SNAP_M of each light (flat
 * [lat, lon, …] degrees). Returns how many nodes got a light.
 */
export function applySignals(g: Graph, signals: Float64Array): number {
  g.signal.fill(0);
  if (g.nodeCount === 0 || signals.length < 2) return 0;
  const proj = new LocalProjection({ lat: g.lat[0] as number, lon: g.lon[0] as number });
  const kx = proj.mPerDegLon;
  const ky = proj.mPerDegLat;
  const lat0 = proj.origin.lat;
  const lon0 = proj.origin.lon;
  const cell = SIGNAL_SNAP_M;
  const key = (cx: number, cy: number) => cx * 73_856_093 + cy * 19_349_663;
  const grid = new Map<number, number[]>();
  for (let n = 0; n < g.nodeCount; n++) {
    const x = ((g.lon[n] as number) - lon0) * kx;
    const y = ((g.lat[n] as number) - lat0) * ky;
    const k = key(Math.floor(x / cell), Math.floor(y / cell));
    const l = grid.get(k);
    if (l) l.push(n);
    else grid.set(k, [n]);
  }
  let count = 0;
  for (let i = 0; i + 1 < signals.length; i += 2) {
    const x = ((signals[i + 1] as number) - lon0) * kx;
    const y = ((signals[i] as number) - lat0) * ky;
    const cx = Math.floor(x / cell);
    const cy = Math.floor(y / cell);
    let best = -1;
    let bestD = SIGNAL_SNAP_M * SIGNAL_SNAP_M;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (const n of grid.get(key(cx + dx, cy + dy)) ?? []) {
          const ex = ((g.lon[n] as number) - lon0) * kx - x;
          const ey = ((g.lat[n] as number) - lat0) * ky - y;
          const d = ex * ex + ey * ey;
          if (d <= bestD) {
            bestD = d;
            best = n;
          }
        }
      }
    }
    if (best >= 0 && g.signal[best] === 0) {
      g.signal[best] = 1;
      count++;
    }
  }
  return count;
}

/**
 * The busiest road class that runs *through* node n (at least two of its road edges meet there),
 * at a junction with at least three edges; Hw.Other (0) when there is no road to cross.
 */
export function crossedRoad(g: Graph, n: number): number {
  const a = g.adjStart[n] as number;
  const b = g.adjStart[n + 1] as number;
  if (b - a < 3) return 0;
  // Edges per road rank (1..7), without allocating: ranks are small.
  RANK_COUNT.fill(0);
  for (let i = a; i < b; i++) {
    const r = ROAD_RANK[g.highway[g.adjEdge[i] as number] as number];
    if (r !== undefined) RANK_COUNT[r] = (RANK_COUNT[r] as number) + 1;
  }
  for (let r = 7; r >= 1; r--) if ((RANK_COUNT[r] as number) >= 2) return RANK_HW[r] as number;
  return 0;
}

/** Per-node cost in metres: road crossing by class, plus SIGNAL_M at a traffic light. */
export function nodePenalties(g: Graph): Float32Array {
  const out = new Float32Array(g.nodeCount);
  for (let n = 0; n < g.nodeCount; n++) {
    const road = crossedRoad(g, n);
    let p = road ? (CROSS_M[road] ?? 0) : 0;
    if (g.signal[n] === 1) {
      // A light on a plain stretch of road (a pedestrian crossing) only stops you if you cross.
      const deg = (g.adjStart[n + 1] as number) - (g.adjStart[n] as number);
      p += deg >= 3 ? SIGNAL_M : SIGNAL_M / 2;
    }
    out[n] = p;
  }
  return out;
}

export interface StreetStats {
  /** Traffic lights at the route's interior nodes. */
  signals: number;
  /** Interior nodes where a tertiary or busier road is crossed. */
  busyCrossings: number;
}

/** Lights within this distance along the route are one junction (OSM maps each corner). */
export const SIGNAL_GROUP_M = 50;

/**
 * Lights and busy-road crossings passed along `edges`, a walk starting at node `start`. Lights
 * (and busy crossings) closer than SIGNAL_GROUP_M along the route count once: one junction.
 */
export function routeStreetStats(g: Graph, edges: ArrayLike<number>, start: number): StreetStats {
  let signals = 0;
  let busyCrossings = 0;
  let n = start;
  let along = 0;
  let lastSignal = -Infinity;
  let lastBusy = -Infinity;
  for (let i = 0; i < edges.length; i++) {
    const e = edges[i] as number;
    n = otherEnd(g, e, n);
    along += g.len[e] as number;
    if (i === edges.length - 1) break; // the end node is not passed through
    if (g.signal[n] === 1) {
      if (along - lastSignal >= SIGNAL_GROUP_M) signals++;
      lastSignal = along;
    }
    const road = crossedRoad(g, n);
    if (road && (ROAD_RANK[road] as number) >= (ROAD_RANK[BUSY] as number)) {
      if (along - lastBusy >= SIGNAL_GROUP_M) busyCrossings++;
      lastBusy = along;
    }
  }
  return { signals, busyCrossings };
}

// ---- Learned runnability prior (ADR 0007) -------------------------------------------------------

/** Feature names, in vector order. Index 0 is the bias. Residential streets are the baseline. */
export const FEATURES = [
  "bias",
  "footway",
  "path",
  "cycleway",
  "service",
  "tertiary",
  "busy_road",
  "steps",
  "unpaved",
  "sidewalk",
  "lit",
  "green",
  "water",
  "trail",
  "crossings",
] as const;
export const N_FEATURES = FEATURES.length;

const HW_FEATURE: Readonly<Partial<Record<number, number>>> = {
  [Hw.Footway]: 1,
  [Hw.Pedestrian]: 1,
  [Hw.Path]: 2,
  [Hw.Track]: 2,
  [Hw.Bridleway]: 2,
  [Hw.Cycleway]: 3,
  [Hw.Service]: 4,
  [Hw.Unclassified]: 4,
  [Hw.Other]: 4,
  [Hw.Tertiary]: 5,
  [Hw.Secondary]: 6,
  [Hw.Primary]: 6,
  [Hw.Steps]: 7,
};

/**
 * Writes edge e's feature vector into `out` (length N_FEATURES). `np` is `nodePenalties(g)`:
 * crossings become metres of crossing cost per 100 m of edge, capped at 3.
 */
export function edgeFeatures(g: Graph, e: number, np: Float32Array, out: Float64Array): void {
  out.fill(0);
  out[0] = 1;
  const hw = g.highway[e] as number;
  const f = HW_FEATURE[hw];
  if (f !== undefined) out[f] = 1;
  const surf = g.surface[e] as number;
  if (surf === Surface.Unpaved || surf === Surface.Compacted) out[8] = 1;
  const flags = g.flags[e] as number;
  if (flags & Flag.Sidewalk && hw >= Hw.LivingStreet && hw <= Hw.Primary) out[9] = 1;
  if (flags & Flag.Lit) out[10] = 1;
  out[11] = g.green[e] as number;
  out[12] = g.water[e] as number;
  out[13] = g.trailS[e] as number;
  const len = Math.max(20, g.len[e] as number);
  const node = 0.5 * ((np[g.u[e] as number] as number) + (np[g.v[e] as number] as number));
  out[14] = Math.min(3, node / len);
}
